import React, { useCallback, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button } from 'ncoded-component-library';
import ResolvePopup from 'components/ResolvePopup';
import PencilIcon from 'icons/Pencil.icon';
import { DataRoomRFI } from 'router/subrouters/DataRoom/models/DataRoomRfi';
import { getFormattedDate } from 'dateUtils';
import useRfiRename from 'router/subrouters/DataRoom/hooks/useRfiRename';
import {
  getIsRfiCreationTypeChecklist,
  getIsRfiOverdue,
} from '../../../RfiList/components/RfiRequestsTable/utils';
import RfiInfoNameEdit from './components/RfiInfoNameEdit';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { getMixpanelErrorProps } from 'axiosUtils';
import { ErrorViewedProps } from 'models/MixpanelEvents';

import './RfiInfo.styles.scss';

type RfiInfoProps = {
  className?: string;
  rfi: DataRoomRFI;
  onRfiUpdate?: (rfi: DataRoomRFI) => void;
};

const RfiInfo: React.FC<RfiInfoProps> = ({ className, rfi, onRfiUpdate }) => {
  const { name, dueDate, creationType, completedCategories, totalCategories } =
    rfi || {};

  const { t } = useTranslation();

  const classes = classNames('rfi-info', className);

  const [isEditing, setIsEditing] = useState(false);

  const [renameRfi, loading] = useRfiRename(rfi, onRfiUpdate);

  const getMixpanelProps = useGetMixpanelProps();

  const onEditClick = useCallback(() => setIsEditing(true), []);

  const onEditClose = useCallback(() => setIsEditing(false), []);

  const onEditSave = useCallback(
    async (value: string) => {
      try {
        await renameRfi(value);
        setIsEditing(false);
      } catch (e) {
        mixpanelService.errorViewed(
          getMixpanelProps(getMixpanelErrorProps(e)) as ErrorViewedProps,
        );
      }
    },
    [renameRfi, getMixpanelProps],
  );

  return (
    <div className={classes}>
      {isEditing ? (
        <RfiInfoNameEdit
          name={name}
          disabled={loading}
          onSave={onEditSave}
          onClose={onEditClose}
        />
      ) : (
        <h1 className="rfi-info__name">
          <span>{name}</span>

          {onRfiUpdate && (
            <ResolvePopup
              className="rfi-info__name-rename-popup"
              onHover
              popupContent={t('rename')}
            >
              <Button
                variant="icon"
                icon={PencilIcon}
                aria-label={t('rename')}
                onClick={(e) => {
                  e.stopPropagation();
                  onEditClick();
                }}
              />
            </ResolvePopup>
          )}
        </h1>
      )}

      <div className="rfi-info__meta">
        <span className="rfi-info__meta__item">
          {t(
            getIsRfiCreationTypeChecklist(creationType)
              ? 'noIndividualFileAssignments'
              : 'filesAssignedIndividually',
          )}
        </span>

        <span className="rfi-info__meta__item">
          {t('filesUploadedCount', {
            completed: completedCategories,
            total: totalCategories,
          })}
        </span>

        {dueDate && (
          <span className="rfi-info__meta__item">
            {t('dueDateAbsolute', { date: getFormattedDate(dueDate) })}
          </span>
        )}

        {rfi && getIsRfiOverdue(rfi) && (
          <span className="rfi-info__meta__overdue">{t('overdue')}</span>
        )}
      </div>
    </div>
  );
};

export default RfiInfo;
